import { Injectable, NgModule } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Resolve,
  Router,
  RouterModule,
  Routes,
} from "@angular/router";
import { map, take } from "rxjs/operators";
import { LoggedInGuard } from "../auth/auth.guards";
import { GeofencesListComponent } from "./geofences-list/geofences-list.component";
import { GeofenceService } from "../geofences/geofence.service";
import { SessionService } from "../session.service";
import { GeofenceComponent } from "./geofence/geofence.component";
import { FacilitiesResolve } from "../facilities/facilities.routes";
import { PermissionService } from "../permission.service";
import { from, Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class GeofenceWriteGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.geofences.hasWrite()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({
  providedIn: "root",
})
export class GeofenceDetailGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.geofences.hasDetail()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({
  providedIn: "root",
})
export class GeofenceListGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.geofences.hasList()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({ providedIn: "root" })
class GeofencesListPermissionResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}
  resolve() {
    return Promise.all([
      this.PermissionService.categories.geofences.hasWrite(),
      this.PermissionService.categories.geofences.hasDetail(),
    ]).then(([hasGeofencesWrite, hasGeofenceDetail]) => {
      return {
        hasGeofencesWrite,
        hasGeofenceDetail,
      };
    });
  }
}

@Injectable({ providedIn: "root" })
class GeofencePermissionResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}
  resolve() {
    return Promise.all([
      this.PermissionService.categories.facilities.hasList(),
    ]).then(([hasFacilitiesList]) => {
      return {
        hasFacilitiesList,
      };
    });
  }
}

@Injectable({ providedIn: "root" })
export class GeofenceResolve implements Resolve<any> {
  constructor(
    private GeofenceService: GeofenceService,
    private SessionService: SessionService,
  ) {}
  resolve(route: ActivatedRouteSnapshot) {
    return this.GeofenceService.getGeofence(
      this.SessionService.currentAccountValue!.accountID,
      route.params.geofenceID,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class GeofencesResolve implements Resolve<any> {
  constructor(
    private GeofenceService: GeofenceService,
    private SessionService: SessionService,
  ) {}
  resolve() {
    return this.GeofenceService.getGeofences(
      this.SessionService.currentAccountValue!.accountID,
      {
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class IntersectingGeofencesResolve implements Resolve<any> {
  constructor(
    private GeofenceService: GeofenceService,
    private SessionService: SessionService,
  ) {}
  resolve(route: ActivatedRouteSnapshot) {
    return this.GeofenceService.getIntersectingGeofences(
      this.SessionService.currentAccountValue!.accountID,
      route.params.geofenceID,
    ).pipe(take(1));
  }
}

const routes: Routes = [
  {
    path: "",
    canActivate: [LoggedInGuard],
    runGuardsAndResolvers: "always",
    data: { sidebar: "infrastructure" },
    children: [
      {
        path: "list",
        component: GeofencesListComponent,
        resolve: {
          // geofences: GeofencesResolve,
          permissions: GeofencesListPermissionResolve,
        },
        data: { title: "Geofences" },
        canActivate: [GeofenceListGuard],
      },
      {
        path: "create",
        component: GeofenceComponent,
        resolve: {
          facilities: FacilitiesResolve,
          permissions: GeofencePermissionResolve,
        },
        data: { title: "Create New Geofence" },
        canActivate: [GeofenceWriteGuard],
      },
      {
        path: ":geofenceID",
        resolve: {
          geofence: GeofenceResolve,
          intersectingGeofences: IntersectingGeofencesResolve,
          facilities: FacilitiesResolve,
          permissions: GeofencePermissionResolve,
        },
        canActivate: [GeofenceWriteGuard, GeofenceDetailGuard],
        runGuardsAndResolvers: "paramsChange",
        children: [
          {
            path: "",
            component: GeofenceComponent,
            data: { title: "Geofence" },
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class GeofencesRoutingModule {}

const geofencesModuleLazyLoadRoutes: Routes = [
  {
    path: "geofences",
    loadChildren: () =>
      import("./geofences.module").then((m) => m.GeofencesModule),
  },
];

export { geofencesModuleLazyLoadRoutes };
